/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { Modal } from '../../../shared/modals/Modal';
import {
  ActivityIndicator,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { settingsStyles as styles } from '../settingsScreen.styles';
import { plural } from '../../../shared/format/plural';
import { modalExperience } from '../../../shared/modals/modalExperience.styles';
import { DenseListCard } from '../../../shared/surface-cards/DenseListCard'
import { StatusPill } from '../../../shared/surface-cards/StatusPill';
import { spacing } from '../../../shared/theme/spacing';
import type { MercadoPagoBranchSettingsScreenViewModel } from '../useMercadoPagoBranchSettingsScreen';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { isBillingEnabled } from '../../../shell/billing/isBillingEnabled';
import type { MercadoPagoPosListItem, MercadoPagoPosPendingItem } from '../../../mercadopago/infra/mercadopagoConnectService';

const MP_CREATE_POS_GUIDE_URL =
  'https://www.mercadopago.com.ar/developers/es/docs/qr-code/create-store-and-pos';
const MP_PANEL_LOCALES_GUIDE_URL =
  'https://www.mercadopago.com.ar/developers/es/docs/mp-point/configure-terminal';
const MP_COBRAR_EN_LOCAL_URL = 'https://www.mercadopago.com.ar/collections?from-section=menu';

function safeTrim(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}

type SetupStep = 'connect' | 'select' | 'ready';

function resolveSetupStep(oauthConnected: boolean, posConfigured: boolean): SetupStep {
  if (posConfigured) return 'ready';
  if (oauthConnected) return 'select';
  return 'connect';
}

const localStyles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'center',
    padding: spacing.lg,
  },
  modalCard: {
    backgroundColor: ticketColors.paper,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    padding: spacing.md,
    maxHeight: '88%',
    width: '100%',
    maxWidth: 560,
    alignSelf: 'center',
  },
  modalScroll: {
    maxHeight: 420,
  },
  mpActionsColumn: {
    gap: spacing.sm,
  },
  mpStatusBanner: {
    borderRadius: 10,
    padding: spacing.md,
    gap: spacing.xs,
    borderWidth: 1,
  },
  mpStatusOk: {
    backgroundColor: 'rgba(74, 222, 128, 0.08)',
    borderColor: 'rgba(74, 222, 128, 0.28)',
  },
  mpStatusPending: {
    backgroundColor: ticketColors.paper,
    borderColor: ticketColors.ruleSoft,
  },
  mpStatusTextOk: {
    fontSize: 15,
    fontWeight: '700',
    color: ticketColors.ok,
  },
  mpStatusTextPending: {
    fontSize: 15,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  mpHintText: {
    fontSize: 13,
    color: ticketColors.ink2,
    lineHeight: 19,
  },
  mpErrorBanner: {
    borderWidth: 1,
    borderColor: ticketColors.bad,
    borderRadius: 10,
    padding: spacing.sm,
    backgroundColor: ticketColors.paper,
    gap: spacing.xs,
  },
  mpErrorText: {
    fontSize: 13,
    color: ticketColors.bad,
    lineHeight: 20,
  },
  upgradeCard: {
    borderWidth: 1,
    borderColor: ticketColors.gold,
    borderRadius: 10,
    padding: spacing.md,
    backgroundColor: ticketColors.paper,
    gap: spacing.sm,
  },
  setupStepsRow: {
    flexDirection: 'row',
    gap: spacing.xs,
    marginBottom: spacing.sm,
  },
  setupStep: {
    flex: 1,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.xs,
    alignItems: 'center',
    gap: 2,
  },
  setupStepActive: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
  },
  setupStepDone: {
    borderColor: 'rgba(74, 222, 128, 0.35)',
    backgroundColor: 'rgba(74, 222, 128, 0.06)',
  },
  setupStepIndex: {
    fontSize: 11,
    fontWeight: '800',
    color: ticketColors.ink3,
    letterSpacing: 0.3,
  },
  setupStepIndexActive: {
    color: ticketColors.gold,
  },
  setupStepIndexDone: {
    color: ticketColors.ok,
  },
  setupStepLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: ticketColors.ink2,
    textAlign: 'center',
  },
  setupStepLabelActive: {
    color: ticketColors.ink,
  },
  assignedBoxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    padding: spacing.md,
  },
  assignedBoxMain: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  assignedBoxTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  assignedBoxMeta: {
    fontSize: 12,
    color: ticketColors.ink3,
  },
  manualPanel: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    padding: spacing.md,
    gap: spacing.sm,
  },
  manualPanelTitle: {
    fontSize: 12,
    fontWeight: '800',
    color: ticketColors.ink3,
    letterSpacing: 0.35,
    textTransform: 'uppercase',
  },
  modalSection: {
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  modalSectionTitle: {
    fontSize: 11,
    fontWeight: '800',
    color: ticketColors.ink3,
    letterSpacing: 0.35,
    textTransform: 'uppercase',
  },
  posCardSelectedWrap: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
  },
  posCardInner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    minHeight: 52,
  },
  posCardMain: {
    flex: 1,
    minWidth: 0,
    gap: 3,
  },
  posCardTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  posCardMeta: {
    fontSize: 12,
    color: ticketColors.ink2,
    lineHeight: 17,
  },
  posCardAction: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
    paddingHorizontal: spacing.sm,
    minWidth: 88,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  posCardActionText: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.gold,
  },
  modalEmptyBlock: {
    gap: spacing.xs,
    padding: spacing.md,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  modalActionsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginTop: spacing.md,
    paddingTop: spacing.sm,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.rule,
  },
  modalActionButton: {
    flexGrow: 1,
    minWidth: 120,
  },
  statsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    marginBottom: spacing.sm,
  },
  savingOverlay: {
    ...StyleSheet.absoluteFill,
    backgroundColor: 'rgba(9, 14, 34, 0.55)',
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
  },
});

type MercadoPagoBranchSettingsPanelProps = {
  vm: MercadoPagoBranchSettingsScreenViewModel;
  variant?: 'screen' | 'inline';
};

function MpGuideLink({ label = 'Guía oficial de Mercado Pago' }: { label?: string }) {
  return (
    <Text
      style={styles.linkText}
      onPress={() => Linking.openURL(MP_CREATE_POS_GUIDE_URL)}
      accessibilityRole="link"
    >
      {label}
    </Text>
  );
}

function SetupStepsBar({ step }: { step: SetupStep }) {
  const steps: Array<{ key: SetupStep; index: string; label: string }> = [
    { key: 'connect', index: '1', label: 'Conectar' },
    { key: 'select', index: '2', label: 'Elegir caja' },
    { key: 'ready', index: '3', label: 'Listo' },
  ];
  const order: SetupStep[] = ['connect', 'select', 'ready'];
  const currentIdx = order.indexOf(step);

  return (
    <View style={localStyles.setupStepsRow}>
      {steps.map((item, idx) => {
        const done = idx < currentIdx;
        const active = item.key === step;
        return (
          <View
            key={item.key}
            style={[
              localStyles.setupStep,
              active && localStyles.setupStepActive,
              done && localStyles.setupStepDone,
            ]}
          >
            <Text
              style={[
                localStyles.setupStepIndex,
                active && localStyles.setupStepIndexActive,
                done && localStyles.setupStepIndexDone,
              ]}
            >
              {done ? '✓' : item.index}
            </Text>
            <Text style={[localStyles.setupStepLabel, active && localStyles.setupStepLabelActive]}>
              {item.label}
            </Text>
          </View>
        );
      })}
    </View>
  );
}

function MpPosReadyRow({
  item,
  selected,
  disabled,
  onPress,
}: {
  item: MercadoPagoPosListItem;
  selected: boolean;
  disabled: boolean;
  onPress: () => void;
}) {
  return (
    <DenseListCard wrapStyle={selected ? localStyles.posCardSelectedWrap : undefined}>
      <Pressable
        disabled={disabled}
        onPress={onPress}
        style={({ pressed }) => [localStyles.posCardInner, pressed && !disabled && { opacity: 0.88 }, disabled && { opacity: 0.6 }]}
        accessibilityRole="button"
        accessibilityLabel={`Usar caja ${item.pos_name ?? item.external_pos_id}`}
        accessibilityState={{ selected }}
      >
        <View style={localStyles.posCardMain}>
          <Text style={localStyles.posCardTitle} numberOfLines={1}>
            {item.pos_name ?? 'Caja QR'}
          </Text>
          <Text style={localStyles.posCardMeta} numberOfLines={2}>
            {item.store_name ?? 'Local'} · ID {item.external_pos_id}
          </Text>
        </View>
        <StatusPill label={selected ? 'Asignada' : 'Lista'} tone={selected ? 'success' : 'neutral'} compact />
        <Ionicons
          name={selected ? 'checkmark-circle' : 'chevron-forward'}
          size={20}
          color={selected ? ticketColors.ok : ticketColors.ink3}
        />
      </Pressable>
    </DenseListCard>
  );
}

function MpPosPendingRow({
  item,
  disabled,
  linking,
  onLink,
}: {
  item: MercadoPagoPosPendingItem;
  disabled: boolean;
  linking: boolean;
  onLink: () => void;
}) {
  return (
    <DenseListCard>
      <View style={localStyles.posCardInner}>
        <View style={localStyles.posCardMain}>
          <Text style={localStyles.posCardTitle} numberOfLines={1}>
            {item.pos_name ?? 'Caja'}
          </Text>
          <Text style={localStyles.posCardMeta} numberOfLines={2}>
            {item.store_name ?? 'Local'} · MP #{item.pos_internal_id}
          </Text>
        </View>
        <TouchableOpacity
          style={[localStyles.posCardAction, (disabled || linking) && styles.disabled]}
          disabled={disabled || linking}
          onPress={onLink}
          accessibilityRole="button"
          accessibilityLabel={`Vincular caja ${item.pos_name ?? item.pos_internal_id}`}
        >
          <Text style={localStyles.posCardActionText}>{linking ? 'Vinculando…' : 'Vincular'}</Text>
        </TouchableOpacity>
      </View>
    </DenseListCard>
  );
}

export function MercadoPagoBranchSettingsPanel({ vm, variant = 'screen' }: MercadoPagoBranchSettingsPanelProps) {
  const posConfigured = Boolean(vm.mpPosConfig?.external_pos_id?.trim());
  const setupStep = resolveSetupStep(vm.mpOAuthConnected, posConfigured);
  const assignedExternalId = safeTrim(vm.mpPosConfig?.external_pos_id);

  if (!vm.mpQrSettingsAllowed) {
    // With billing off, entitlements unlock mp_qr_pos — this branch is billing-on paywall only.
    // billing disabled — free mode: no "Ver planes" CTA (keep Billing navigation code in VM).
    if (!isBillingEnabled()) {
      return (
        <View style={styles.card}>
          {variant === 'inline' ? <Text style={styles.cardTitle}>Mercado Pago QR</Text> : null}
          <Text style={styles.caption}>Mercado Pago QR está apagado por ahora.</Text>
        </View>
      );
    }
    return (
      <View style={styles.card}>
        {variant === 'inline' ? <Text style={styles.cardTitle}>Mercado Pago QR</Text> : null}
        <View style={localStyles.upgradeCard}>
          <Text style={styles.captionStrong}>Mercado Pago QR</Text>
          <Text style={styles.caption}>Conectá Mercado Pago y elegí la caja QR de este local.</Text>
          <TouchableOpacity
            style={styles.primaryButton}
            onPress={vm.handleUpgradePlan}
            accessibilityRole="button"
            accessibilityLabel="Ver planes y facturación"
          >
            <Text style={styles.primaryButtonText}>Ver planes</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <>
      <View style={styles.card}>
        {variant === 'inline' ? <Text style={styles.cardTitle}>Mercado Pago QR</Text> : null}

        <SetupStepsBar step={setupStep} />

        <View
          style={[
            localStyles.mpStatusBanner,
            posConfigured ? localStyles.mpStatusOk : localStyles.mpStatusPending,
          ]}
        >
          <Text style={posConfigured ? localStyles.mpStatusTextOk : localStyles.mpStatusTextPending}>
            {posConfigured
              ? 'Listo para cobrar con QR'
              : vm.mpOAuthConnected
                ? 'Cuenta conectada. Elegí la caja de este local.'
                : 'Conectá tu cuenta Mercado Pago'}
          </Text>
          <Text style={localStyles.mpHintText}>
            {posConfigured
              ? 'Los cobros QR de este local van a la caja de abajo.'
              : vm.mpOAuthConnected
                ? 'Cada local usa una caja distinta. Si tenés varias, elegí la correcta.'
                : 'Solo el dueño puede conectar la cuenta. Los cobros van a producción.'}
          </Text>
        </View>

        {posConfigured ? (
          <View style={localStyles.assignedBoxRow}>
            <View style={localStyles.assignedBoxMain}>
              <Text style={localStyles.assignedBoxTitle}>Caja asignada</Text>
              <Text style={localStyles.assignedBoxMeta} numberOfLines={1}>
                ID externo · {assignedExternalId}
              </Text>
            </View>
            <StatusPill label="Activa" tone="success" compact />
          </View>
        ) : null}

        <View style={localStyles.mpActionsColumn}>
          {!vm.mpOAuthConnected ? (
            <TouchableOpacity
              style={[styles.primaryButton, (vm.mpOAuthOpening || !vm.canEdit) && styles.disabled]}
              onPress={vm.handleConnectMercadoPago}
              disabled={vm.mpOAuthOpening || !vm.canEdit}
              accessibilityLabel="Conectar cuenta Mercado Pago"
              accessibilityRole="button"
            >
              <Text style={styles.primaryButtonText}>
                {vm.mpOAuthOpening ? 'Abriendo Mercado Pago…' : 'Conectar Mercado Pago'}
              </Text>
            </TouchableOpacity>
          ) : (
            <>
              <TouchableOpacity
                style={[styles.primaryButton, (vm.mpPosListLoading || !vm.canEdit) && styles.disabled]}
                onPress={vm.handleOpenMercadoPagoPosPicker}
                disabled={vm.mpPosListLoading || !vm.canEdit}
                accessibilityLabel="Elegir caja QR de Mercado Pago"
                accessibilityRole="button"
              >
                <Text style={styles.primaryButtonText}>
                  {vm.mpPosListLoading ? 'Buscando cajas…' : posConfigured ? 'Cambiar caja' : 'Elegir caja'}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.secondaryButton, (vm.mpOAuthOpening || !vm.canEdit) && styles.disabled]}
                onPress={vm.handleConnectMercadoPago}
                disabled={vm.mpOAuthOpening || !vm.canEdit}
                accessibilityLabel="Reconectar cuenta Mercado Pago"
                accessibilityRole="button"
              >
                <Text style={styles.secondaryButtonText}>
                  {vm.mpOAuthOpening ? 'Abriendo Mercado Pago…' : 'Reconectar cuenta'}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.secondaryButton, (vm.mpDisconnecting || !vm.canEdit) && styles.disabled, { borderColor: ticketColors.bad }]}
                onPress={() => vm.setMpDisconnectConfirmOpen(true)}
                disabled={vm.mpDisconnecting || !vm.canEdit}
                accessibilityLabel="Desvincular Mercado Pago"
                accessibilityRole="button"
              >
                <Text style={[styles.secondaryButtonText, { color: ticketColors.bad }]}>
                  {vm.mpDisconnecting ? 'Desvinculando…' : 'Desvincular'}
                </Text>
              </TouchableOpacity>
            </>
          )}
        </View>

        {vm.mpPosListError ? (
          <View style={localStyles.mpErrorBanner}>
            <Text style={localStyles.mpErrorText}>{vm.mpPosListError}</Text>
            <TouchableOpacity
              onPress={vm.dismissMpPosListError}
              accessibilityRole="button"
              accessibilityLabel="Ocultar aviso"
            >
              <Text style={[styles.caption, { color: ticketColors.gold, fontWeight: '600' }]}>Ocultar</Text>
            </TouchableOpacity>
          </View>
        ) : null}

        <View style={localStyles.manualPanel}>
          <Text style={localStyles.manualPanelTitle}>Pegar el ID a mano</Text>
          <Text style={localStyles.mpHintText}>
            Si ya tenés el ID de la caja en Mercado Pago, pegalo acá. En el panel de Mercado Pago activá{' '}
            <Text style={styles.captionStrong}>Cobrar con monto cerrado</Text>.{' '}
            <Text style={styles.linkText} onPress={() => Linking.openURL(MP_COBRAR_EN_LOCAL_URL)}>
              Abrir panel de Mercado Pago
            </Text>
          </Text>
          <TextInput
            style={styles.input}
            value={vm.mpPosExternalIdInput}
            onChangeText={vm.setMpPosExternalIdInput}
            placeholder="Ej: CAJA001"
            placeholderTextColor={ticketColors.ink2}
            autoCapitalize="none"
            editable={vm.canEdit}
          />
          <TouchableOpacity
            style={[styles.secondaryButton, (vm.mpPosSaving || !vm.canEdit) && styles.disabled]}
            disabled={vm.mpPosSaving || !vm.canEdit || !safeTrim(vm.mpPosExternalIdInput)}
            onPress={vm.handleSaveMpPosConfig}
            accessibilityLabel="Guardar ID de la caja"
            accessibilityRole="button"
          >
            <Text style={styles.secondaryButtonText}>
              {vm.mpPosSaving ? 'Guardando…' : 'Guardar ID'}
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      <MercadoPagoBranchSettingsModals vm={vm} assignedExternalId={assignedExternalId} />
    </>
  );
}

function MpPosEmptyGuide() {
  return (
    <View style={localStyles.modalEmptyBlock}>
      <Text style={styles.captionStrong}>¿No aparece ninguna caja?</Text>
      <Text style={localStyles.mpHintText}>
        1. En mercadopago.com.ar: <Text style={styles.captionStrong}>Herramientas de ventas</Text> →{' '}
        <Text style={styles.captionStrong}>Cobrar en tu local</Text> →{' '}
        <Text style={styles.linkText} onPress={() => Linking.openURL(MP_COBRAR_EN_LOCAL_URL)}>
          Configurar
        </Text>
      </Text>
      <Text style={localStyles.mpHintText}>
        2. Creá un local y al menos una caja (ej. “Caja 1”).
      </Text>
      <Text style={localStyles.mpHintText}>
        3. En <Text style={styles.captionStrong}>Configurar caja</Text>, activá{' '}
        <Text style={styles.captionStrong}>Cobrar con monto cerrado</Text> y guardá.
      </Text>
      <Text style={localStyles.mpHintText}>
        4. Volvé acá y usá <Text style={styles.captionStrong}>Actualizar lista</Text>.
      </Text>
      <Text style={localStyles.mpHintText}>
        <Text style={styles.linkText} onPress={() => Linking.openURL(MP_PANEL_LOCALES_GUIDE_URL)}>
          Guía del panel
        </Text>
        {' · '}
        <MpGuideLink label="Guía API" />
      </Text>
    </View>
  );
}

export function MercadoPagoBranchSettingsModals({
  vm,
  assignedExternalId,
}: {
  vm: MercadoPagoBranchSettingsScreenViewModel;
  assignedExternalId?: string;
}) {
  const hasReady = vm.mpPosPickerItems.length > 0;
  const hasPending = vm.mpPosPickerPendingItems.length > 0;
  const hasAny = hasReady || hasPending;
  const hasMixed = hasReady && hasPending;
  const totalCount = vm.mpPosListMeta?.pos_total ?? vm.mpPosPickerItems.length + vm.mpPosPickerPendingItems.length;

  const modalSubtitle = !hasAny
    ? 'Buscamos cajas en tu cuenta conectada.'
    : hasReady && !hasPending
      ? 'Elegí la caja para este local.'
      : !hasReady && hasPending
        ? 'Estas cajas están en Mercado Pago. Falta vincularlas con Vuelto.'
        : 'Elegí una caja lista o vinculá una del panel de Mercado Pago.';

  return (
    <>
    <Modal
      visible={vm.mpPosPickerOpen}
      animationType="slide"
      transparent
      onRequestClose={() => {
        if (!vm.mpPosPickerSaving) vm.setMpPosPickerOpen(false);
      }}
    >
      <View style={localStyles.modalOverlay}>
        <View style={localStyles.modalCard}>
          <View style={[modalExperience.heroBand, { marginBottom: spacing.sm }]}>
            <View style={modalExperience.heroText}>
              <Text style={modalExperience.heroKicker}>Mercado Pago</Text>
              <Text style={modalExperience.heroTitle}>Elegir caja QR</Text>
              <Text style={modalExperience.heroSubtitle}>{modalSubtitle}</Text>
            </View>
            {totalCount > 0 ? (
              <View style={modalExperience.countBadge}>
                <Text style={modalExperience.countValue}>{totalCount}</Text>
                <Text style={modalExperience.countLabel}>caja{totalCount === 1 ? '' : 's'}</Text>
              </View>
            ) : null}
          </View>

          {hasMixed && vm.mpPosListMeta?.pos_total ? (
            <View style={localStyles.statsRow}>
              <StatusPill
                label={`${vm.mpPosListMeta.pos_with_external_id ?? 0} ${plural(vm.mpPosListMeta.pos_with_external_id ?? 0, 'lista', 'listas')}`}
                tone="neutral"
                compact
              />
              <StatusPill
                label={`${vm.mpPosListMeta.pos_pending_link ?? 0} sin vincular`}
                tone="warning"
                compact
              />
            </View>
          ) : null}

          <ScrollView style={localStyles.modalScroll} keyboardShouldPersistTaps="handled">
            {!hasAny ? <MpPosEmptyGuide /> : null}

            {hasReady ? (
              <View style={localStyles.modalSection}>
                <Text style={localStyles.modalSectionTitle}>
                  {vm.mpPosPickerItems.length === 1 ? 'Caja disponible' : 'Elegí una caja'}
                </Text>
                {vm.mpPosPickerItems.map((item) => (
                  <MpPosReadyRow
                    key={`ready-${item.store_id}-${item.external_pos_id}`}
                    item={item}
                    selected={assignedExternalId === item.external_pos_id}
                    disabled={vm.mpPosPickerSaving}
                    onPress={() => vm.handleSelectMercadoPagoPos(item)}
                  />
                ))}
              </View>
            ) : null}

            {hasPending ? (
              <View style={localStyles.modalSection}>
                <Text style={localStyles.modalSectionTitle}>
                  {hasReady
                    ? 'En Mercado Pago. Vinculalas antes de usarlas.'
                    : vm.mpPosPickerPendingItems.length === 1
                      ? 'Vincular con Vuelto'
                      : 'Elegí cuál vincular'}
                </Text>
                {!hasReady ? (
                  <Text style={[localStyles.mpHintText, { marginBottom: spacing.xs }]}>
                    Le ponemos el ID y activamos monto cerrado en Mercado Pago.
                  </Text>
                ) : null}
                {vm.mpPosPickerPendingItems.map((item) => (
                  <MpPosPendingRow
                    key={`pending-${item.pos_internal_id}`}
                    item={item}
                    disabled={!vm.canEdit || vm.mpPosPickerSaving}
                    linking={vm.linkingPosInternalId === item.pos_internal_id}
                    onLink={() => vm.handleLinkMercadoPagoPos(item)}
                  />
                ))}
              </View>
            ) : null}
          </ScrollView>

          <View style={localStyles.modalActionsRow}>
            <TouchableOpacity
              style={[styles.secondaryButton, localStyles.modalActionButton, vm.mpPosListLoading && styles.disabled]}
              onPress={vm.handleRefreshMercadoPagoPosPicker}
              disabled={vm.mpPosListLoading || vm.mpPosPickerSaving}
              accessibilityRole="button"
            >
              <Text style={styles.secondaryButtonText}>
                {vm.mpPosListLoading ? 'Actualizando…' : 'Actualizar lista'}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.secondaryButton, localStyles.modalActionButton, vm.mpPosPickerSaving && styles.disabled]}
              onPress={() => vm.setMpPosPickerOpen(false)}
              disabled={vm.mpPosPickerSaving}
              accessibilityRole="button"
            >
              <Text style={styles.secondaryButtonText}>Cerrar</Text>
            </TouchableOpacity>
          </View>

          {vm.mpPosPickerSaving ? (
            <View style={localStyles.savingOverlay} pointerEvents="none">
              <ActivityIndicator color={ticketColors.gold} />
              <Text style={localStyles.mpHintText}>Guardando caja…</Text>
            </View>
          ) : null}
        </View>
      </View>
    </Modal>

    <Modal
      visible={vm.mpDisconnectConfirmOpen}
      animationType="fade"
      transparent
      onRequestClose={() => {
        if (!vm.mpDisconnecting) vm.setMpDisconnectConfirmOpen(false);
      }}
    >
      <Pressable
        style={localStyles.modalOverlay}
        onPress={() => {
          if (!vm.mpDisconnecting) vm.setMpDisconnectConfirmOpen(false);
        }}
      >
        <View style={localStyles.modalCard} onStartShouldSetResponder={() => true}>
          <Text style={modalExperience.heroTitle}>Desvincular Mercado Pago</Text>
          <Text style={[localStyles.mpHintText, { marginTop: spacing.sm }]}>
            Se van a eliminar de Vuelto los tokens de acceso y la caja QR asignada. Para volver a
            cobrar con QR vas a tener que conectar Mercado Pago de nuevo.
          </Text>
          <Text style={[localStyles.mpHintText, { marginTop: spacing.xs }]}>
            Para que Mercado Pago deje de reconocer la conexión, también conviene revocar Vuelto
            desde <Text style={styles.captionStrong}>Configuración → Aplicaciones autorizadas</Text>{' '}
            en tu cuenta de Mercado Pago.
          </Text>
          <View style={[localStyles.modalActionsRow, { borderTopWidth: 0, marginTop: spacing.md }]}>
            <TouchableOpacity
              style={[styles.secondaryButton, localStyles.modalActionButton, vm.mpDisconnecting && styles.disabled]}
              onPress={() => vm.setMpDisconnectConfirmOpen(false)}
              disabled={vm.mpDisconnecting}
              accessibilityRole="button"
            >
              <Text style={styles.secondaryButtonText}>Cancelar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.primaryButton, localStyles.modalActionButton, vm.mpDisconnecting && styles.disabled, { backgroundColor: ticketColors.bad }]}
              onPress={vm.handleDisconnectMercadoPago}
              disabled={vm.mpDisconnecting}
              accessibilityRole="button"
              accessibilityLabel="Confirmar desvinculación de Mercado Pago"
            >
              <Text style={styles.primaryButtonText}>
                {vm.mpDisconnecting ? 'Desvinculando…' : 'Desvincular'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </Pressable>
    </Modal>
    </>
  );
}
